import * as React from 'react';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import StyledModal from '../Components/StyledModal';

export interface AskAIProps {
  accessToken: string;
  onConfirm: (data: string) => void;
}

/**
 * Component Modal for AI chat and response
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-05-20
 * @lastUpdated
 * @version 1.1.0
 */
export const AskAI = (props: AskAIProps): React.JSX.Element => {
  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.accessToken);

  const promptRef = React.useRef(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [response, setResponse] = React.useState<string>('');
  const [isAsking, setIsAsking] = React.useState<boolean>(false);
  const [htmlContent, setHtmlContent] = React.useState<string>('');

  const promptTextAppend =
    'I do not want a subject just the body and use my custom merge tags please. For the clients or prospects name use {{FIRSTNAME}}. If i am making an SMS or text message use {{MY-NAME-FIRST}} {{MY-NAME-LAST}} {{MY-COMPANY-NAME}} for my information. If its an email use  {{SIG-EMAIL}} at the bottom. Do not return any markdown or code just pure text.';

  /**
   * Handles the AI prompt submission:
   * - Extracts and validates the input text from `promptRef`
   * - Calls OpenAI via the web API
   * - Updates the raw and HTML-formatted response
   */
  const handleSubmit = (): void => {
    const promptText = promptRef.current?.innerText?.trim();
    if (!promptText) return;

    setIsAsking(true);

    webAPI.getOpenAIChat({ chatBody: promptText + promptTextAppend }).then((response) => {
      const content = response.choices[0].message.content;

      setResponse(content);

      const formattedHtml = content
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line) => `<p>${line}</p>`)
        .join('');

      setHtmlContent(formattedHtml);
      setIsAsking(false);
    });
  };

  /**
   * Handles the confirmation of the AI-generated response:
   * - Calls the optional `onConfirm` callback with the selected response
   * - Closes the modal and clears both raw and HTML-formatted response
   *
   * @param response - The AI-generated response to confirm and use
   */
  const handleUseResponse = (response: string): void => {
    props.onConfirm?.(response);
    setIsOpen(false);
    setResponse('');
    setHtmlContent('');
  };

  return (
    <div>
      {!isOpen && (
        <div className="badge badge-pill badge-info" onClick={() => setIsOpen(true)}>
          <i className="far fa-sparkles"></i> Ask AI
        </div>
      )}
      {isOpen && (
        <StyledModal
          size="lg"
          title="Ask AI"
          footerCloseButton={true}
          show={isOpen}
          onClose={() => setIsOpen(false)}
        >
          {/* Prompt input */}
          <label className="label">Type anything...</label>
          <div
            className="rounded border p-2 mb-2"
            contentEditable
            ref={promptRef}
            style={{ minHeight: '80px', outline: 'none' }}
          ></div>

          {/* Submit */}
          <div className="text-end mb-3">
            {!isAsking && (
              <span className="badge badge-primary badge-pill px-3 py-2 cp" onClick={handleSubmit}>
                <i className="fas fa-paper-plane mr-1"></i> Submit
              </span>
            )}
            {isAsking && (
              <>
                <i className="far fa-spinner fa-spin"></i> loading...
              </>
            )}
          </div>

          {/*  AI Response / Use Response */}
          {response && (
            <>
              <div
                className="border rounded p-2 mb-2"
                style={{ minHeight: '100px', whiteSpace: 'pre-wrap' }}
                dangerouslySetInnerHTML={{
                  __html: response
                    ? response.replace(/\n/g, '<br />')
                    : 'AI response will appear here.',
                }}
              ></div>

              <div className="text-end">
                <span
                  className="badge badge-success badge-pill px-3 py-2 cp"
                  onClick={() => handleUseResponse(htmlContent)}
                >
                  <i className="fas fa-check-circle mr-1"></i> Use This Response
                </span>
              </div>
            </>
          )}
        </StyledModal>
      )}
    </div>
  );
};
